// @flow
import React, { Component, useRef, useCallback } from 'react';
import DatePickerWithTimeDropDown from '@justpark/ui/src/components/DatePickerWithTimeDropDown';
import { useTranslation } from 'react-i18next';
import localDateTimeIso8601 from '@justpark/helpers/src/localDateTimeIso8601/localDateTimeIso8601';
import bem from '@justpark/helpers/src/bem/bem';
import useOutsideClickHandler from '../../../hooks/useOutsideClickHandler';
import Brand from '../../UI/Brand';
import blockDatesBeforeCurrentDate from '../../../helpers/dates/blockDatesBeforeCurrentDate';
import blockBeforeCurrentDateAfterYear from '../../../helpers/dates/blockBeforeCurrentDateAfterYear';
import getDayjsInstance from '../../../helpers/dates/getDayjsInstance';
import convertToDayjs from '../../../helpers/dates/convertToDayjs';

import '../../../../static/scss/components/datePicker.scss';

type InitialState = {
  enabled: boolean
};

const initialState = {
  enabled: true
};

type BothProps = {
  onClose: () => void,
  onDateChange: () => void,
  isOpen: boolean,
  activeTab: string
};

type Props = BothProps & {
  arriving?: string,
  leaving?: string,
  showTimePicker?: boolean,
  checkTime?: () => void
};

type DatePickerWithOutsideClickProps = BothProps & {
  showTimePicker: boolean,
  checkTime: () => void
};

const DatePickerWithOutsideClick = (props: DatePickerWithOutsideClickProps) => {
  const node = useRef();

  const {
    onClose,
    showTimePicker,
    onDateChange,
    checkTime,
    isOpen,
    activeTab
  } = props;

  const outsideClickOnClose = useCallback(() => {
    checkTime();
    onClose();
  }, [onClose, checkTime]);

  useOutsideClickHandler(node, outsideClickOnClose, isOpen);

  const { t } = useTranslation();
  const buttonText = t('search:datePickerButton', 'Done');

  return (
    <Brand>
      <div ref={node} className="c-datepicker">
        {[
          {
            label: 'Arriving',
            active: activeTab === 'from'
          },
          {
            label: 'Leaving',
            active: activeTab === 'to'
          }
        ].map((item) => {
          const { active, label } = item;

          if (!active) {
            return null;
          }

          const key = label;

          const minDate = blockDatesBeforeCurrentDate();

          const maxDate =
            activeTab === 'from' ? blockBeforeCurrentDateAfterYear() : '';

          const dateInstance = props[label.toLowerCase()];
          const isLeaving = activeTab === 'to';

          return (
            <div
              className={bem('c-date-picker-with-time-dropdown', null, {
                isLeaving
              })}
              key={key}
            >
              <DatePickerWithTimeDropDown
                onDateChange={(newDate) =>
                  onDateChange(label.toLowerCase(), convertToDayjs(newDate))
                }
                date={dateInstance.format(localDateTimeIso8601)}
                showTimePicker={showTimePicker}
                onClose={() => {
                  checkTime();
                  onClose();
                }}
                buttonText={buttonText}
                minDate={minDate}
                maxDate={maxDate}
                timeText={
                  isLeaving
                    ? t('search:exitBefore', 'Exit before')
                    : t('search:enter', 'Enter after')
                }
                isLeaving={isLeaving}
              />
            </div>
          );
        })}
      </div>
    </Brand>
  );
};

type State = InitialState & {
  tabs: number
};

export type Which = 'arriving' | 'leaving';

class DatePicker extends Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = initialState;
  }

  render() {
    const {
      onClose,
      checkTime,
      showTimePicker,
      onDateChange,
      arriving,
      leaving,
      isOpen,
      activeTab
    } = this.props;

    return (
      <DatePickerWithOutsideClick
        onClose={onClose}
        checkTime={checkTime}
        onDateChange={onDateChange}
        showTimePicker={showTimePicker}
        arriving={arriving}
        leaving={leaving}
        isOpen={isOpen}
        activeTab={activeTab}
      />
    );
  }
}

DatePicker.defaultProps = {
  arriving: getDayjsInstance(),
  leaving: getDayjsInstance(),
  checkTime: () => {},
  showTimePicker: true
};

export default DatePicker;
